import { useEffect, useState } from 'react';
import { AutoComplete, Button, Form, Input, Checkbox, Tooltip } from 'antd';
import { EnvironmentOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { CityAsyncSelect } from '@/components/pos/CityAsyncSelect';
import { WardAsyncSelect } from '@/components/pos/WardAsyncSelect';
import { FloatingField } from './FloatingField';
import { SyncIconButton } from './SyncIconButton';
import { AddressBookModal } from './AddressBookModal';
import { useCustomerAddresses } from '@/hooks/useCustomers';
import {
  buildDeliveryNote,
  deliveryAddressName,
  entryToDeliveryValues,
  isDeliveryEdited,
  matchRecipient,
  takeDeliverySnapshot,
  type AddressBookEntry,
  type DeliveryAddressSource,
  type DeliveryRecipient,
} from './addressBook';
import {
  deliveryPhoneRules,
  deliveryCitizenIdRules,
  deliveryEmailRules,
  requiredRule,
  isMaskedCccd,
} from './customerFormSchema';

interface Props {
  form: FormInstance;
  cityId?: string;
  cccdMasked?: boolean;
  onRevealCccd?: () => void;
  /**
   * TCR-2128 — `customerEditAbilities(...).canEditDeliveryAddress`: được thêm/sửa entry trong
   * Sổ địa chỉ và thấy ô "Lưu vào Sổ địa chỉ". Thiếu quyền vẫn chọn & áp dụng được.
   */
  canEditAddressBook?: boolean;
}

/** So khớp không phân biệt hoa thường & dấu (cùng cách `addressBook.ts` lọc sổ). */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

export function DeliveryRecipientFields({
  form,
  cityId,
  cccdMasked,
  onRevealCccd,
  canEditAddressBook = false,
}: Props) {
  // Đặt field đích rồi validate ngay để lỗi định dạng (nếu có) hiện tức thì —
  // giống InvoiceSection.
  const setAndValidate = (targetName: string, value: unknown) => {
    form.setFieldValue(targetName, value);
    form.validateFields([targetName]).catch(() => undefined);
  };

  const deliveryCitizenId = Form.useWatch('delivery_citizen_id', form);
  const isCccdMasked = cccdMasked || isMaskedCccd(deliveryCitizenId);

  const same = Form.useWatch('delivery_same_as_customer', form);
  const customerName = Form.useWatch('customer_name', form);
  const customerPhone = Form.useWatch('customer_phone', form);
  const customerEmail = Form.useWatch('customer_email', form);
  const citizenId = Form.useWatch('citizen_id', form);
  const customerAddress = Form.useWatch('customer_address', form);
  const customerCityId = Form.useWatch('customer_city_id', form);
  const customerWardId = Form.useWatch('customer_ward_id', form);
  const cityName = Form.useWatch('_cityName', form);
  const wardName = Form.useWatch('_wardName', form);
  const description = Form.useWatch('description', form);

  // TCR-2128 — Sổ địa chỉ. `preserve: true` vì các field này không có Form.Item trong dialog.
  const customerId = Form.useWatch('customer_user_id', { form, preserve: true }) as
    | string
    | undefined;
  const addressSource = Form.useWatch('_deliveryAddressSource', { form, preserve: true }) as
    | DeliveryAddressSource
    | undefined;
  const { data: bookData } = useCustomerAddresses(customerId || undefined);
  const bookCount = bookData?.length ?? 0;
  const [bookOpen, setBookOpen] = useState(false);
  const bookDisabled = Boolean(same) || !customerId;

  // Theo dõi đủ field so sánh để ô "Lưu vào Sổ địa chỉ" bật/tắt ngay khi gõ.
  const deliveryValues: Record<string, unknown> = {
    delivery_name: Form.useWatch('delivery_name', { form, preserve: true }),
    delivery_phone: Form.useWatch('delivery_phone', { form, preserve: true }),
    delivery_email: Form.useWatch('delivery_email', { form, preserve: true }),
    delivery_citizen_id: deliveryCitizenId,
    delivery_city_id: Form.useWatch('delivery_city_id', { form, preserve: true }),
    delivery_ward_id: Form.useWatch('delivery_ward_id', { form, preserve: true }),
    delivery_address: Form.useWatch('delivery_address', { form, preserve: true }),
  };
  // TCR-2384 — tên điểm giao của địa chỉ đang áp (rỗng khi chưa chọn từ sổ hoặc đã sửa tay sang chỗ khác).
  const addressName = same
    ? ''
    : deliveryAddressName({ ...deliveryValues, _deliveryAddressSource: addressSource });
  const showSaveToBook =
    canEditAddressBook && !same && Boolean(customerId) && isDeliveryEdited(deliveryValues, addressSource);

  // TCR-2384 — gợi ý = người nhận của địa chỉ đang áp, lọc theo chữ đang gõ (tên không dấu / SĐT chứa).
  // Gõ tự do vẫn giữ nguyên chữ: AutoComplete chỉ gợi ý, không ép chọn.
  const recipientOptions = (typed: unknown) => {
    const q = fold(String(typed ?? ''));
    return (addressSource?.recipients ?? [])
      .filter((r) => !q || fold(r.name).includes(q) || r.phone.includes(q))
      .map((r: DeliveryRecipient, i) => ({
        // `value` PHẢI duy nhất: rc-select tra option theo value, trùng tên/SĐT thì option cuối
        // thắng ⇒ chọn người đầu mà điền SĐT người sau. Tên/SĐT thật do `pickRecipient` ghi.
        key: `${i}-${r.phone}`,
        value: `${i}-${r.phone}`,
        recipient: r,
        label: `${r.name}${r.note ? ` · ${r.note}` : ''} — ${r.phone}`,
      }));
  };
  const pickRecipient = (_: string, option: { recipient?: DeliveryRecipient }) => {
    const r = option.recipient;
    if (!r) return;
    form.setFieldsValue({ delivery_name: r.name, delivery_phone: r.phone });
    // Ghi chú tự điền, trừ khi thu ngân đã sửa tay.
    if (!form.getFieldValue('_deliveryNoteTouched')) {
      form.setFieldValue(
        'delivery_note',
        buildDeliveryNote(addressSource?.note, addressSource?.recipients ?? [], r.phone),
      );
    }
    form.validateFields(['delivery_name', 'delivery_phone']).catch(() => undefined);
  };

  // Ô "Thêm vào người nhận khác": người vừa gõ chưa có trong địa chỉ đang áp.
  const typedName = String(deliveryValues.delivery_name ?? '').trim();
  const typedPhone = String(deliveryValues.delivery_phone ?? '').trim();
  const showAddReceiver =
    canEditAddressBook &&
    !same &&
    Boolean(addressSource?.address_id) &&
    Boolean(typedName) &&
    Boolean(typedPhone) &&
    !matchRecipient(addressSource, typedName, typedPhone);
  const bookEntry = (bookData as AddressBookEntry[] | undefined)?.find(
    (e) => e.address_id === addressSource?.address_id,
  );
  const addressLabel = bookEntry?.contact_code || bookEntry?.name || addressSource?.snapshot.delivery_name || '';

  const applyEntry = (entry: AddressBookEntry) => {
    form.setFieldsValue(entryToDeliveryValues(entry));
    form.validateFields(['delivery_name', 'delivery_phone']).catch(() => undefined);
    setBookOpen(false);
  };

  useEffect(() => {
    if (!same) return;
    const mirrored = {
      delivery_name: customerName ?? '',
      delivery_phone: customerPhone ?? '',
      delivery_citizen_id: citizenId ?? '',
      delivery_email: customerEmail ?? '',
      delivery_city_id: customerCityId ?? undefined,
      _deliveryCityName: cityName ?? '',
      delivery_ward_id: customerWardId ?? undefined,
      _deliveryWardName: wardName ?? '',
      delivery_address: customerAddress ?? '',
      delivery_note: description ?? '',
    };
    form.setFieldsValue({
      ...mirrored,
      _deliveryNoteTouched: false,
      delivery_add_receiver_to_book: false,
      // TCR-2128 — đổ tự động, không phải sửa tay: ghi nguồn (không gắn entry nào) để bỏ tick
      // "Giống…" xong không bật ngay ô "Lưu vào Sổ địa chỉ".
      _deliveryAddressSource: { snapshot: takeDeliverySnapshot(mirrored) },
      delivery_save_to_book: false,
    });
    form.validateFields(['delivery_name', 'delivery_phone']).catch(() => undefined);
  }, [
    same,
    customerName,
    customerPhone,
    customerEmail,
    citizenId,
    customerAddress,
    customerCityId,
    customerWardId,
    cityName,
    wardName,
    description,
    form,
  ]);

  return (
    <>
      <div className="delivery-same-row">
        <Form.Item name="delivery_same_as_customer" valuePropName="checked">
          <Checkbox>Giống thông tin khách hàng</Checkbox>
        </Form.Item>
        <Tooltip
          title={
            !customerId
              ? 'Chọn khách hàng có hồ sơ để dùng Sổ địa chỉ'
              : same
                ? 'Bỏ tick "Giống thông tin khách hàng" để chọn từ Sổ địa chỉ'
                : undefined
          }
        >
          <Button
            size="small"
            className="addr-book-btn"
            data-testid="address-book-button"
            icon={bookCount > 0 ? <EnvironmentOutlined /> : <PlusOutlined />}
            disabled={bookDisabled}
            onClick={() => setBookOpen(true)}
          >
            {bookCount > 0 ? (
              <>
                Sổ địa chỉ<span className="addr-book-count">{bookCount}</span>
              </>
            ) : (
              'Thêm địa chỉ'
            )}
          </Button>
        </Tooltip>
      </div>

      {addressName ? (
        <div className="delivery-address-name" data-testid="delivery-address-name">
          Điểm giao: <strong>{addressName}</strong>
        </div>
      ) : null}

      <FloatingField label="Họ tên" name="delivery_name" rules={[requiredRule('họ tên người nhận')]} required>
        <AutoComplete
          options={recipientOptions(deliveryValues.delivery_name)}
          onSelect={pickRecipient}
          popupMatchSelectWidth={false}
        >
          <Input
            suffix={
              <SyncIconButton
                tooltip="Đồng bộ từ họ tên khách hàng"
                onClick={() => setAndValidate('delivery_name', form.getFieldValue('customer_name') ?? '')}
              />
            }
          />
        </AutoComplete>
      </FloatingField>

      <FloatingField label="Số điện thoại" name="delivery_phone" rules={deliveryPhoneRules} required>
        <AutoComplete
          options={recipientOptions(deliveryValues.delivery_phone)}
          onSelect={pickRecipient}
          popupMatchSelectWidth={false}
        >
          <Input
            maxLength={11}
            suffix={
              <SyncIconButton
                tooltip="Đồng bộ từ SĐT khách hàng"
                onClick={() => setAndValidate('delivery_phone', form.getFieldValue('customer_phone') ?? '')}
              />
            }
          />
        </AutoComplete>
      </FloatingField>

      <FloatingField label="CCCD" name="delivery_citizen_id" rules={deliveryCitizenIdRules}>
        <Input
          maxLength={12}
          suffix={
            isCccdMasked && onRevealCccd ? (
              <Tooltip title="Nhấp để giải mã & sửa CCCD">
                <EyeOutlined
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                  onClick={onRevealCccd}
                />
              </Tooltip>
            ) : (
              <SyncIconButton
                tooltip="Đồng bộ từ CCCD khách hàng"
                onClick={() => setAndValidate('delivery_citizen_id', form.getFieldValue('citizen_id') ?? '')}
              />
            )
          }
        />
      </FloatingField>

      <FloatingField label="Email" name="delivery_email" rules={deliveryEmailRules}>
        <Input
          suffix={
            <SyncIconButton
              tooltip="Đồng bộ từ email khách hàng"
              onClick={() => setAndValidate('delivery_email', form.getFieldValue('customer_email') ?? '')}
            />
          }
        />
      </FloatingField>

      <FloatingField label="Thành phố" name="delivery_city_id">
        <CityAsyncSelect
          placeholder="Chọn thành phố"
          onChange={(v) => {
            form.setFieldValue('delivery_city_id', v);
            form.setFieldsValue({ delivery_ward_id: undefined });
          }}
          onChangeWithLabel={(_, name) => form.setFieldValue('_deliveryCityName', name)}
        />
      </FloatingField>

      <FloatingField label="Phường / xã" name="delivery_ward_id">
        <WardAsyncSelect
          cityId={cityId ?? ''}
          placeholder="Chọn phường / xã"
          onChange={(v) => form.setFieldValue('delivery_ward_id', v)}
          onChangeWithLabel={(_, name) => form.setFieldValue('_deliveryWardName', name)}
        />
      </FloatingField>

      <FloatingField
        label="Địa chỉ"
        name="delivery_address"
        suffix={
          <SyncIconButton
            tooltip="Đồng bộ từ địa chỉ khách hàng"
            onClick={() => setAndValidate('delivery_address', form.getFieldValue('customer_address') ?? '')}
          />
        }
      >
        <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} />
      </FloatingField>

      <FloatingField
        label="Ghi chú"
        name="delivery_note"
        suffix={
          <SyncIconButton
            tooltip="Đồng bộ từ ghi chú khách hàng"
            onClick={() => {
              form.setFieldValue('_deliveryNoteTouched', true);
              setAndValidate('delivery_note', form.getFieldValue('description') ?? '');
            }}
          />
        }
      >
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 6 }}
          onChange={() => form.setFieldValue('_deliveryNoteTouched', true)}
        />
      </FloatingField>

      {showSaveToBook ? (
        <div className="delivery-save-to-book">
          <Form.Item name="delivery_save_to_book" valuePropName="checked">
            <Checkbox>
              {addressSource?.address_id
                ? 'Lưu thay đổi vào Sổ địa chỉ của KH'
                : 'Lưu địa chỉ này vào Sổ địa chỉ của KH'}
            </Checkbox>
          </Form.Item>
          <span className="delivery-save-to-book-hint">Không tick: chỉ dùng cho đơn này</span>
        </div>
      ) : null}

      {showAddReceiver ? (
        <div className="delivery-save-to-book">
          <Form.Item name="delivery_add_receiver_to_book" valuePropName="checked">
            <Checkbox>{`Thêm "${typedName} ${typedPhone}" vào người nhận khác của ${addressLabel}`}</Checkbox>
          </Form.Item>
          <span className="delivery-save-to-book-hint">Không tick: chỉ dùng cho đơn này</span>
        </div>
      ) : null}

      {customerId ? (
        <AddressBookModal
          open={bookOpen}
          customerId={customerId}
          customerName={customerName}
          currentAddressId={addressSource?.address_id}
          canManage={canEditAddressBook}
          onApply={applyEntry}
          onClose={() => setBookOpen(false)}
        />
      ) : null}
    </>
  );
}
